import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  EyeOutlined,
  FileSearchOutlined,
  FilterOutlined,
  LineChartOutlined,
  ReloadOutlined,
  SyncOutlined,
  UndoOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import useIbkrBrokerLiveState, {
  extractBrokerLiveErrorMessage,
} from '../../hooks/useIbkrBrokerLiveState';
import useIbkrFlexConnection, { extractFlexErrorMessage } from '../../hooks/useIbkrFlexConnection';
import useIbkrImportOperations, {
  ACTIVE_IMPORT_STATES,
  extractIbkrOperationsErrorMessage,
  normalizeImportStatus,
} from '../../hooks/useIbkrImportOperations';
import TradingViewChart from '../journal/TradingViewChart';
import ImportBrokerCSVFlyin from './ImportBrokerCSVFlyin';

// sync_status values that mean a sync is currently in flight.
const ACTIVE_SYNC_STATES = ['queued', 'running', 'processing'];

const formatTimestamp = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const connectionBanner = (status) => {
  switch (status.connection_status) {
    case 'connected':
      return {
        severity: 'success',
        icon: <CheckCircleOutlined />,
        text: 'Historical Flex credentials are stored and verified.',
      };
    case 'error':
      return {
        severity: 'error',
        icon: <CloseCircleOutlined />,
        text: status.last_test_message || 'The last connection test failed. Check the credentials and try again.',
      };
    case 'untested':
      return {
        severity: 'warning',
        icon: <WarningOutlined />,
        text: 'Credentials are saved but not verified yet. Run Test Connection to confirm they work.',
      };
    case 'not_connected':
    default:
      return {
        severity: 'info',
        icon: <ApiOutlined />,
        text: 'No IBKR Flex credentials on file for historical statement sync.',
      };
  }
};

const syncStatusChip = (syncStatus) => {
  if (!syncStatus) return null;
  const map = {
    queued: { label: 'Sync queued', color: 'default', icon: <ClockCircleOutlined /> },
    running: { label: 'Sync running', color: 'info', icon: <SyncOutlined spin /> },
    processing: { label: 'Sync processing', color: 'info', icon: <SyncOutlined spin /> },
    succeeded: { label: 'Last sync succeeded', color: 'success', icon: <CheckCircleOutlined /> },
    failed: { label: 'Last sync failed', color: 'error', icon: <CloseCircleOutlined /> },
  };
  const entry = map[syncStatus] || { label: syncStatus, color: 'default', icon: null };
  return <Chip size="small" color={entry.color} icon={entry.icon || undefined} label={entry.label} />;
};

const formatStatusLabel = (value) => {
  if (!value) return 'Unavailable';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const liveStateMeta = (value, { stale = false } = {}) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'auth_expired') {
    return { color: 'error', severity: 'error', icon: <CloseCircleOutlined />, label: 'Auth expired' };
  }
  if (normalized === 'degraded') {
    return { color: 'warning', severity: 'warning', icon: <WarningOutlined />, label: 'Degraded' };
  }
  if (['failed', 'error', 'unavailable', 'disconnected'].includes(normalized)) {
    return {
      color: normalized === 'disconnected' ? 'default' : 'warning',
      severity: normalized === 'disconnected' ? 'info' : 'warning',
      icon: <CloseCircleOutlined />,
      label: formatStatusLabel(value),
    };
  }
  if (stale || normalized === 'stale') {
    return { color: 'warning', severity: 'warning', icon: <ClockCircleOutlined />, label: 'Stale' };
  }
  if (['healthy', 'connected', 'live', 'available'].includes(normalized)) {
    return { color: 'success', severity: 'success', icon: <CheckCircleOutlined />, label: formatStatusLabel(value) };
  }
  if (['queued', 'running', 'processing'].includes(normalized)) {
    return { color: 'info', severity: 'info', icon: <SyncOutlined spin />, label: formatStatusLabel(value) };
  }
  return { color: 'default', severity: 'info', icon: null, label: formatStatusLabel(value) };
};

const statusChip = (value, label, options = {}) => {
  const meta = liveStateMeta(value, options);
  return (
    <Chip
      size="small"
      color={meta.color}
      icon={meta.icon || undefined}
      label={`${label}: ${meta.label}`}
      variant={meta.color === 'default' ? 'outlined' : 'filled'}
    />
  );
};

const countText = (count, singular, plural = `${singular}s`) => {
  if (!Number.isFinite(Number(count))) return null;
  const n = Number(count);
  return `${n} ${n === 1 ? singular : plural}`;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat().format(n);
};

const formatDecimal = (value, digits = 2) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 2,
  });
};

const formatShortTimestamp = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const importStatusMeta = (value) => {
  const normalized = normalizeImportStatus(value);
  const map = {
    queued: { color: 'default', severity: 'info', icon: <ClockCircleOutlined />, label: 'Queued' },
    running: { color: 'info', severity: 'info', icon: <SyncOutlined spin />, label: 'Running' },
    processing: { color: 'info', severity: 'info', icon: <SyncOutlined spin />, label: 'Processing' },
    succeeded: { color: 'success', severity: 'success', icon: <CheckCircleOutlined />, label: 'Succeeded' },
    failed: { color: 'error', severity: 'error', icon: <CloseCircleOutlined />, label: 'Failed' },
  };
  return map[normalized] || {
    color: normalized ? 'default' : 'default',
    severity: normalized ? 'info' : 'warning',
    icon: null,
    label: normalized ? formatStatusLabel(normalized) : 'Unavailable',
  };
};

const importStatusChip = (status, label = 'Status') => {
  const meta = importStatusMeta(status);
  return (
    <Chip
      size="small"
      color={meta.color}
      icon={meta.icon || undefined}
      label={`${label}: ${meta.label}`}
      variant={meta.color === 'default' ? 'outlined' : 'filled'}
    />
  );
};

const countChip = (label, value, options = {}) => {
  if (!Number.isFinite(Number(value))) return null;
  return (
    <Chip
      key={options.key}
      size="small"
      variant="outlined"
      color={options.color || 'default'}
      label={`${label}: ${formatNumber(value)}`}
    />
  );
};

const SENSITIVE_DETAIL_KEY_RE = /(^|[_-])(token|secret|password|credential)([_-]|$)|flex[_-]?query|query[_-]?id/i;

const isSensitiveDisplayKey = (key) => SENSITIVE_DETAIL_KEY_RE.test(String(key || ''));

const maskCredentialValue = (value) => {
  if (!value) return '••••';
  const str = String(value);
  if (str.includes('•') || str.includes('*')) return str;
  if (str.length <= 4) return '••••';
  return `••••${str.slice(-4)}`;
};

const formatDetailValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return formatDecimal(value, 4);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const primitiveValues = value.filter((item) => ['string', 'number', 'boolean'].includes(typeof item));
    return primitiveValues.length ? primitiveValues.join(', ') : `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  if (typeof value === 'object') return 'Available';
  return String(value);
};

const safeDetailEntries = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  return Object.entries(value)
    .filter(([key, entry]) => !isSensitiveDisplayKey(key) && key !== 'raw' && entry !== undefined && entry !== null && entry !== '')
    .slice(0, 12);
};

const detailValue = (row, keys, fallback = '—') => {
  if (!row || typeof row !== 'object') return fallback;
  const keyList = Array.isArray(keys) ? keys : [keys];
  for (const key of keyList) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
};

const summarizeOperationResponse = (payload, fallback) => {
  if (!payload || typeof payload !== 'object') return fallback;
  return (
    payload.message ||
    payload.detail ||
    payload.status_message ||
    payload.summary?.message ||
    payload.result?.message ||
    fallback
  );
};

const collectImpactCounts = (source) => {
  if (!source) return [];
  const counts = source.counts || source.impact_summary?.counts || source.impact?.counts || {};
  const direct = source.impact || source.impact_summary || source;
  return [
    ['Rows', counts.staged ?? direct.staging_rows],
    ['Processed', counts.processed],
    ['Normalized', counts.normalized],
    ['Orders', counts.orders ?? direct.orders],
    ['Executions', counts.executions ?? direct.executions],
    ['Lot closures', counts.lotClosures ?? direct.lot_closures],
    ['Journal entries', counts.journalEntries ?? direct.journal_entries],
    ['Journal allocations', counts.journalAllocations ?? direct.journal_allocations],
    ['Journal notes', counts.journalNotes ?? direct.journal_notes],
    ['Recent resets', counts.recentExecutionsReset ?? direct.recent_executions_reset],
    ['Errors', counts.errors ?? direct.errors],
    ['Deleted', counts.deleted],
  ].filter(([, value]) => Number.isFinite(Number(value)));
};

export default function IbkrFlexConnectionFlyin({ open, onClose, onConnected, onOpenCsvImport }) {
  const { status, loading, error, pendingAction, refreshStatus, connect, test, sync, disconnect } =
    useIbkrFlexConnection();
  const {
    connection: liveConnection,
    positions: livePositions,
    accountState,
    accountStateStatus,
    recentExecutions,
    executionsStatus,
    syncStatus: liveSyncStatus,
    loading: liveLoading,
    errors: liveErrors,
    pendingAction: livePendingAction,
    reload: reloadLiveState,
    refreshPositions,
    refreshExecutions,
  } = useIbkrBrokerLiveState({ enabled: open });
  const importOperations = useIbkrImportOperations({
    enabled: open,
    preferredImportVersion: status.last_import_version,
  });

  const [form, setForm] = useState({ flexToken: '', queryId: '', accountId: '', label: '', flexVersion: '' });
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionNotice, setActionNotice] = useState('');
  const [operationError, setOperationError] = useState('');
  const [operationNotice, setOperationNotice] = useState('');
  const [csvOpen, setCsvOpen] = useState(false);
  const [ledgerFilters, setLedgerFilters] = useState({
    symbol: '',
    dateFrom: '',
    dateTo: '',
    side: 'all',
    status: 'all',
    errorState: 'all',
  });
  const [confirmAction, setConfirmAction] = useState(null);

  const containerRef = useRef(null);
  const prevConnectedRef = useRef(false);

  useEffect(() => {
    if (open) {
      refreshStatus();
      const t = setTimeout(() => containerRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    setShowForm(false);
    setForm({ flexToken: '', queryId: '', accountId: '', label: '', flexVersion: '' });
    setFormError('');
    setActionError('');
    setActionNotice('');
    setOperationError('');
    setOperationNotice('');
    setLedgerFilters({ symbol: '', dateFrom: '', dateTo: '', side: 'all', status: 'all', errorState: 'all' });
    setConfirmAction(null);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (status.connected && !prevConnectedRef.current && typeof onConnected === 'function') {
      onConnected(status);
    }
    prevConnectedRef.current = status.connected;
  }, [status, onConnected]);

  useEffect(() => {
    if (!open) return;
    if (status.credentials_stored) setShowForm(false);
  }, [open, status.credentials_stored]);

  const banner = useMemo(() => connectionBanner(status), [status]);
  const syncing = pendingAction === 'sync' || ACTIVE_SYNC_STATES.includes(status.sync_status);
  const hasExternalCsvHandler = typeof onOpenCsvImport === 'function';

  const handleOpenCsvFallback = () => {
    if (hasExternalCsvHandler) {
      onOpenCsvImport();
      onClose?.();
    } else {
      setCsvOpen(true);
    }
  };

  const handleFieldChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSaveCredentials = async () => {
    setFormError('');
    setActionError('');
    setActionNotice('');
    if (!form.flexToken.trim() || !form.queryId.trim()) {
      setFormError('Flex token and query ID are both required.');
      return;
    }
    const result = await connect({
      flexToken: form.flexToken.trim(),
      queryId: form.queryId.trim(),
      accountId: form.accountId.trim() || undefined,
      label: form.label.trim() || undefined,
      flexVersion: form.flexVersion.trim() || undefined,
    });
    if (result.ok) {
      // Never keep raw credential values around once the backend has them.
      setForm({ flexToken: '', queryId: '', accountId: '', label: '', flexVersion: '' });
      setShowForm(false);
      setActionNotice('Credentials saved. Run Test Connection to verify them.');
    } else {
      setActionError(extractFlexErrorMessage(result.error, 'Failed to save credentials.'));
    }
  };

  const handleTest = async () => {
    setActionError('');
    setActionNotice('');
    const result = await test();
    if (result.ok) {
      setActionNotice(
        result.data?.connection_status === 'connected'
          ? 'Connection verified.'
          : 'Test finished — see status above for the result.'
      );
    } else {
      setActionError(extractFlexErrorMessage(result.error, 'Test Connection failed.'));
    }
  };

  const handleSync = async () => {
    setActionError('');
    setActionNotice('');
    const result = await sync();
    if (result.ok) {
      setActionNotice('Sync started. This panel will update automatically as it progresses.');
    } else {
      setActionError(extractFlexErrorMessage(result.error, 'Failed to start sync.'));
    }
  };

  const handleDisconnect = async () => {
    setActionError('');
    setActionNotice('');
    const result = await disconnect();
    if (result.ok) {
      setActionNotice('IBKR Flex connection removed.');
      setShowForm(true);
    } else {
      setActionError(extractFlexErrorMessage(result.error, 'Failed to remove the connection.'));
    }
  };

  const sideOptions = useMemo(() => {
    const values = new Set(importOperations.ledgerRows.map((row) => row.side).filter(Boolean));
    return Array.from(values).sort();
  }, [importOperations.ledgerRows]);

  const statusOptions = useMemo(() => {
    const values = new Set(importOperations.ledgerRows.map((row) => row.status).filter(Boolean));
    return Array.from(values).sort();
  }, [importOperations.ledgerRows]);

  const importVersionOptions = useMemo(() => {
    const options = importOperations.imports.slice();
    const selected = importOperations.selectedImportVersion;
    if (selected && !options.some((item) => String(item.import_version) === String(selected))) {
      options.unshift({
        id: `selected-${selected}`,
        import_version: selected,
        status: importOperations.selectedRefreshStatus.status || 'unknown',
      });
    }
    return options;
  }, [importOperations.imports, importOperations.selectedImportVersion, importOperations.selectedRefreshStatus.status]);

  const filteredLedgerRows = useMemo(() => {
    const symbol = ledgerFilters.symbol.trim().toLowerCase();
    const fromMs = ledgerFilters.dateFrom ? Date.parse(`${ledgerFilters.dateFrom}T00:00:00`) : null;
    const toMs = ledgerFilters.dateTo ? Date.parse(`${ledgerFilters.dateTo}T23:59:59.999`) : null;

    return importOperations.ledgerRows.filter((row) => {
      if (symbol && !String(row.symbol || '').toLowerCase().includes(symbol)) return false;
      if (ledgerFilters.side !== 'all' && String(row.side || '') !== ledgerFilters.side) return false;
      if (ledgerFilters.status !== 'all' && String(row.status || '') !== ledgerFilters.status) return false;
      if (ledgerFilters.errorState === 'errors' && !row.has_error) return false;
      if (ledgerFilters.errorState === 'clean' && row.has_error) return false;
      if (fromMs || toMs) {
        const rowMs = Date.parse(row.timestamp || '');
        if (Number.isNaN(rowMs)) return false;
        if (fromMs && rowMs < fromMs) return false;
        if (toMs && rowMs > toMs) return false;
      }
      return true;
    });
  }, [importOperations.ledgerRows, ledgerFilters]);

  const handleLedgerFilterChange = (field) => (event) => {
    setLedgerFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleImportVersionChange = (event) => {
    setOperationError('');
    setOperationNotice('');
    importOperations.clearTradeDetail();
    importOperations.setSelectedImportVersion(event.target.value);
  };

  const handleRefreshOperations = async () => {
    setOperationError('');
    setOperationNotice('');
    await importOperations.reload();
    setOperationNotice('Import operations refreshed.');
  };

  const handleSelectLedgerRow = async (row) => {
    const tradeId = row.trade_id;
    setOperationError('');
    setOperationNotice('');
    if (!tradeId) {
      setOperationError('This ledger row does not include a backend trade id.');
      return;
    }
    const detail = await importOperations.loadTradeDetail(tradeId);
    if (!detail) {
      setOperationError('Could not load the selected trade detail.');
    }
  };

  const openRollbackSelectedConfirm = () => {
    if (!importOperations.selectedImportVersion) return;
    setConfirmAction({
      type: 'rollback-import',
      title: 'Rollback selected import?',
      confirmLabel: 'Rollback import',
      description: `This removes backend-created artifacts for import version ${importOperations.selectedImportVersion}.`,
      importVersion: importOperations.selectedImportVersion,
      impact: importOperations.selectedImport?.impact_summary || importOperations.selectedImport,
    });
  };

  const openRollbackLatestConfirm = () => {
    const latest = importOperations.latestImport || importOperations.selectedImport;
    setConfirmAction({
      type: 'rollback-latest',
      title: 'Rollback latest IBKR import?',
      confirmLabel: 'Rollback latest',
      description: 'This asks the backend to roll back the latest IBKR import artifacts available to this account.',
      importVersion: latest?.import_version,
      impact: latest?.impact_summary || latest,
    });
  };

  const openDeleteTradeConfirm = () => {
    const trade = importOperations.tradeDetail;
    if (!trade?.trade_id) return;
    setConfirmAction({
      type: 'delete-trade',
      title: 'Delete selected trade?',
      confirmLabel: 'Delete trade',
      description: `This removes the selected backend trade${trade.symbol ? ` for ${trade.symbol}` : ''}.`,
      tradeId: trade.trade_id,
      impact: trade.impact_summary || trade,
    });
  };

  const handleConfirmDangerAction = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setOperationError('');
    setOperationNotice('');

    let result;
    if (action.type === 'rollback-import') {
      result = await importOperations.rollbackSelectedImport(action.importVersion);
    } else if (action.type === 'rollback-latest') {
      result = await importOperations.rollbackLatestImport();
    } else if (action.type === 'delete-trade') {
      result = await importOperations.removeTrade(action.tradeId);
    }

    if (result?.ok) {
      setConfirmAction(null);
      setOperationNotice(summarizeOperationResponse(result.data, 'Operation completed.'));
      return;
    }
    setOperationError(extractIbkrOperationsErrorMessage(result?.error, 'Operation failed.'));
  };

  const renderMaskedCredentials = () => (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">Stored credentials</Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {status.label ? <Chip size="small" label={`Label: ${status.label}`} variant="outlined" /> : null}
        <Chip size="small" label={`Token: ${maskCredentialValue(status.token)}`} variant="outlined" />
        <Chip size="small" label={`Query ID: ${maskCredentialValue(status.query_id)}`} variant="outlined" />
        {status.account_id ? <Chip size="small" label={`Account: ${status.account_id}`} variant="outlined" /> : null}
      </Stack>
    </Stack>
  );

  const renderCredentialForm = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Find your Flex token and query ID under IBKR Client Portal &rarr; Reports &rarr; Flex Queries. We only ever
        display masked values back to you once they&apos;re saved.
      </Typography>
      <TextField
        label="Flex token"
        type="password"
        autoComplete="off"
        value={form.flexToken}
        onChange={handleFieldChange('flexToken')}
        fullWidth
        required
      />
      <TextField
        label="Query ID"
        autoComplete="off"
        value={form.queryId}
        onChange={handleFieldChange('queryId')}
        fullWidth
        required
      />
      <TextField
        label="Label (optional)"
        placeholder="e.g. Main IBKR account"
        autoComplete="off"
        value={form.label}
        onChange={handleFieldChange('label')}
        fullWidth
      />
      <TextField
        label="Account ID (optional)"
        autoComplete="off"
        value={form.accountId}
        onChange={handleFieldChange('accountId')}
        fullWidth
      />
      <TextField
        label="Flex version (optional)"
        autoComplete="off"
        value={form.flexVersion}
        onChange={handleFieldChange('flexVersion')}
        fullWidth
      />
      {formError ? <Alert severity="error">{formError}</Alert> : null}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {status.credentials_stored ? (
          <Button onClick={() => setShowForm(false)} disabled={pendingAction === 'connect'}>Cancel</Button>
        ) : null}
        <Button
          variant="contained"
          onClick={handleSaveCredentials}
          disabled={pendingAction === 'connect'}
          startIcon={pendingAction === 'connect' ? <CircularProgress size={16} color="inherit" /> : <ApiOutlined />}
        >
          Save credentials
        </Button>
      </Stack>
    </Stack>
  );

  const renderLastSync = () => {
    const hasAny =
      status.last_sync_started_at ||
      status.last_sync_finished_at ||
      status.sync_status ||
      status.last_task_id ||
      status.last_import_version ||
      status.last_rows_staged ||
      status.last_sync_error_code;
    if (!hasAny) return null;
    return (
      <Stack spacing={1}>
        <Divider />
        <Typography variant="overline" color="text.secondary">Last sync</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {syncStatusChip(status.sync_status)}
          <Chip size="small" variant="outlined" label={`Started: ${formatTimestamp(status.last_sync_started_at)}`} />
          <Chip size="small" variant="outlined" label={`Finished: ${formatTimestamp(status.last_sync_finished_at)}`} />
          {status.last_task_id ? <Chip size="small" variant="outlined" label={`Task: ${status.last_task_id}`} /> : null}
          {status.last_import_version ? (
            <Chip size="small" variant="outlined" label={`Import version: ${status.last_import_version}`} />
          ) : null}
          {Number.isFinite(status.last_rows_staged) ? (
            <Chip size="small" variant="outlined" label={`Rows staged: ${status.last_rows_staged}`} />
          ) : null}
          {status.last_sync_error_code ? (
            <Chip size="small" color="error" variant="outlined" label={`Error: ${status.last_sync_error_code}`} />
          ) : null}
        </Stack>
        {status.last_sync_message ? (
          <Typography variant="caption" color="text.secondary">{status.last_sync_message}</Typography>
        ) : null}
      </Stack>
    );
  };

  const renderSnapshotRow = ({ title, description, snapshot, countLabel, action }) => {
    const meta = liveStateMeta(snapshot?.data_status, { stale: snapshot?.stale });
    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2">{title}</Typography>
            <Typography variant="caption" color="text.secondary">{description}</Typography>
          </Box>
          {action}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Chip
            size="small"
            color={meta.color}
            icon={meta.icon || undefined}
            label={meta.label}
            variant={meta.color === 'default' ? 'outlined' : 'filled'}
          />
          {snapshot?.source_label ? <Chip size="small" variant="outlined" label={snapshot.source_label} /> : null}
          {snapshot?.connection_state ? statusChip(snapshot.connection_state, 'Connection') : null}
          {snapshot?.as_of ? <Chip size="small" variant="outlined" label={`As of: ${formatTimestamp(snapshot.as_of)}`} /> : null}
          {snapshot?.stale ? <Chip size="small" color="warning" label="Stale" /> : null}
          {countLabel ? <Chip size="small" variant="outlined" label={countLabel} /> : null}
        </Stack>
        {snapshot?.message ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {snapshot.message}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const renderBrokerLiveState = () => {
    const connectionMeta = liveStateMeta(liveConnection.health_state, { stale: liveConnection.stale });
    const liveErrorEntries = Object.entries(liveErrors || {});
    const positionCount = countText(livePositions.count, 'position');
    const positionCountLabel = positionCount || (livePositions.available ? 'No live positions reported' : null);
    const displayedAccountState = accountStateStatus.available ? accountStateStatus : accountState;
    const displayedExecutions = executionsStatus.available ? executionsStatus : recentExecutions;
    const accountStateCount = countText(displayedAccountState.count, 'account');
    const recentExecutionCount = countText(displayedExecutions.count, 'execution');

    return (
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="overline" color="text.secondary">Broker-live state</Typography>
            <Typography variant="body2" color="text.secondary">
              Backend-owned live connection health, live snapshots, account-state availability, and near-live execution provenance.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={reloadLiveState}
            disabled={liveLoading}
            startIcon={liveLoading ? <CircularProgress size={16} /> : <ReloadOutlined />}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            Refresh status
          </Button>
        </Stack>

        <Alert severity={connectionMeta.severity} icon={connectionMeta.icon || undefined}>
          Broker-live connection is {connectionMeta.label.toLowerCase()}.
          {liveConnection.last_success_at ? ` Last success: ${formatTimestamp(liveConnection.last_success_at)}.` : ''}
          {liveConnection.checked_at ? ` Last checked: ${formatTimestamp(liveConnection.checked_at)}.` : ''}
          {liveConnection.auth_expired || liveConnection.reauth_required ? ' Re-authentication is required before live data can refresh.' : ''}
          {liveConnection.degraded ? ' Live data may be partial while the backend connection is degraded.' : ''}
        </Alert>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {statusChip(liveConnection.connection_state, 'Connection', { stale: liveConnection.stale })}
          {statusChip(liveConnection.health_state, 'Health', { stale: liveConnection.stale })}
          {liveConnection.last_success_at ? (
            <Chip size="small" variant="outlined" label={`Last success: ${formatTimestamp(liveConnection.last_success_at)}`} />
          ) : null}
          {liveConnection.checked_at ? (
            <Chip size="small" variant="outlined" label={`Last checked: ${formatTimestamp(liveConnection.checked_at)}`} />
          ) : null}
          {liveConnection.account_ids.length ? (
            <Chip size="small" variant="outlined" label={`Accounts: ${liveConnection.account_ids.join(', ')}`} />
          ) : null}
        </Stack>

        {renderSnapshotRow({
          title: 'Open Positions source',
          description: 'Shows whether Open Positions can use a broker-live snapshot or must fall back to journal/import data.',
          snapshot: livePositions,
          countLabel: positionCountLabel,
          action: (
            <Button
              size="small"
              variant="text"
              onClick={refreshPositions}
              disabled={livePendingAction === 'positions'}
              startIcon={livePendingAction === 'positions' ? <CircularProgress size={16} /> : <SyncOutlined />}
            >
              Refresh positions
            </Button>
          ),
        })}

        {renderSnapshotRow({
          title: 'Account state',
          description: 'Backend account-state availability for live dashboard context.',
          snapshot: displayedAccountState,
          countLabel: accountStateCount,
        })}

        {renderSnapshotRow({
          title: 'Near-live executions',
          description: 'Recent execution availability is provisional until historical Flex or CSV imports reconcile it.',
          snapshot: displayedExecutions,
          countLabel: recentExecutionCount,
          action: (
            <Button
              size="small"
              variant="text"
              onClick={refreshExecutions}
              disabled={livePendingAction === 'executions'}
              startIcon={livePendingAction === 'executions' ? <CircularProgress size={16} /> : <SyncOutlined />}
            >
              Refresh executions
            </Button>
          ),
        })}

        {renderSnapshotRow({
          title: 'Broker-live sync status',
          description: 'Live-service sync state, separate from historical Flex statement sync below.',
          snapshot: liveSyncStatus,
        })}

        {liveErrorEntries.length ? (
          <Alert severity="warning">
            Some broker-live endpoints are unavailable:{' '}
            {liveErrorEntries
              .map(([key, err]) => `${formatStatusLabel(key)}: ${extractBrokerLiveErrorMessage(err, 'unavailable')}`)
              .join('; ')}
          </Alert>
        ) : null}
      </Stack>
    );
  };

  const renderImportCountChips = (importItem) => {
    const counts = importItem?.counts || {};
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {countChip('Rows', counts.staged)}
        {countChip('Processed', counts.processed)}
        {countChip('Normalized', counts.normalized)}
        {countChip('Orders', counts.orders)}
        {countChip('Executions', counts.executions)}
        {countChip('Lot closures', counts.lotClosures)}
        {countChip('Journal entries', counts.journalEntries)}
        {countChip('Journal allocations', counts.journalAllocations)}
        {countChip('Journal notes', counts.journalNotes)}
        {countChip('Errors', counts.errors, { color: counts.errors > 0 ? 'error' : 'default' })}
        {countChip('Deleted', counts.deleted)}
      </Stack>
    );
  };

  const renderImportStatusOverview = () => {
    const latest = importOperations.latestImport;
    const selectedStatus =
      importOperations.selectedRefreshStatus.status ||
      importOperations.selectedImport?.status ||
      importOperations.allRefreshStatus.status;
    const selectedMeta = importStatusMeta(selectedStatus);
    const selectedMessage =
      importOperations.selectedRefreshStatus.message ||
      importOperations.selectedImport?.message ||
      importOperations.selectedImport?.error_message ||
      importOperations.allRefreshStatus.message;
    const activeImport = ACTIVE_IMPORT_STATES.includes(normalizeImportStatus(selectedStatus));

    if (importOperations.importsLoading && !latest) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Loading import operations…</Typography>
        </Stack>
      );
    }

    if (!latest) {
      return (
        <Alert severity="info">
          No IBKR import batches were returned by the backend yet. Run Flex sync or use CSV import to create one.
        </Alert>
      );
    }

    return (
      <Stack spacing={1.5}>
        {activeImport ? <LinearProgress /> : null}
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
          <Stack spacing={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2">Latest import #{latest.import_version}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Started {formatShortTimestamp(latest.started_at || latest.created_at)}
                  {latest.finished_at ? ` · Finished ${formatShortTimestamp(latest.finished_at)}` : ''}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                {importStatusChip(latest.status, 'Latest')}
                {importStatusChip(selectedStatus, 'Selected')}
              </Stack>
            </Stack>
            {renderImportCountChips(latest)}
            {latest.error_code ? <Chip size="small" color="error" variant="outlined" label={`Error: ${latest.error_code}`} sx={{ alignSelf: 'flex-start' }} /> : null}
            {selectedMessage ? <Alert severity={selectedMeta.severity}>{selectedMessage}</Alert> : null}
          </Stack>
        </Paper>
      </Stack>
    );
  };

  const renderLedgerFilters = () => (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="ibkr-import-version-label">Import version</InputLabel>
          <Select
            labelId="ibkr-import-version-label"
            label="Import version"
            value={importOperations.selectedImportVersion || ''}
            onChange={handleImportVersionChange}
            disabled={!importVersionOptions.length}
            MenuProps={{ disablePortal: true, container: () => containerRef.current, keepMounted: true }}
          >
            {!importVersionOptions.length ? <MenuItem value="">No imports</MenuItem> : null}
            {importVersionOptions.map((item) => (
              <MenuItem key={item.id} value={item.import_version}>
                #{item.import_version} · {formatStatusLabel(item.status || 'unknown')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Symbol"
          value={ledgerFilters.symbol}
          onChange={handleLedgerFilterChange('symbol')}
          sx={{ minWidth: 130 }}
        />
        <TextField
          size="small"
          label="From"
          type="date"
          value={ledgerFilters.dateFrom}
          onChange={handleLedgerFilterChange('dateFrom')}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          size="small"
          label="To"
          type="date"
          value={ledgerFilters.dateTo}
          onChange={handleLedgerFilterChange('dateTo')}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="ibkr-side-filter-label">Side</InputLabel>
          <Select
            labelId="ibkr-side-filter-label"
            label="Side"
            value={ledgerFilters.side}
            onChange={handleLedgerFilterChange('side')}
            MenuProps={{ disablePortal: true, container: () => containerRef.current, keepMounted: true }}
          >
            <MenuItem value="all">All sides</MenuItem>
            {sideOptions.map((side) => <MenuItem key={side} value={side}>{side}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="ibkr-status-filter-label">Status</InputLabel>
          <Select
            labelId="ibkr-status-filter-label"
            label="Status"
            value={ledgerFilters.status}
            onChange={handleLedgerFilterChange('status')}
            MenuProps={{ disablePortal: true, container: () => containerRef.current, keepMounted: true }}
          >
            <MenuItem value="all">All statuses</MenuItem>
            {statusOptions.map((entry) => <MenuItem key={entry} value={entry}>{formatStatusLabel(entry)}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="ibkr-error-filter-label">Errors</InputLabel>
          <Select
            labelId="ibkr-error-filter-label"
            label="Errors"
            value={ledgerFilters.errorState}
            onChange={handleLedgerFilterChange('errorState')}
            MenuProps={{ disablePortal: true, container: () => containerRef.current, keepMounted: true }}
          >
            <MenuItem value="all">All rows</MenuItem>
            <MenuItem value="errors">Errors only</MenuItem>
            <MenuItem value="clean">Clean only</MenuItem>
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="text"
          startIcon={<FilterOutlined />}
          onClick={() => setLedgerFilters({ symbol: '', dateFrom: '', dateTo: '', side: 'all', status: 'all', errorState: 'all' })}
        >
          Reset filters
        </Button>
      </Stack>
    </Stack>
  );

  const renderLedgerTable = () => {
    if (importOperations.ledgerLoading) {
      return (
        <Stack spacing={1}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">Loading selected import ledger…</Typography>
        </Stack>
      );
    }

    return (
      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 380 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Symbol</TableCell>
                <TableCell>Side</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Fees</TableCell>
                <TableCell align="right">PnL</TableCell>
                <TableCell>Linkage</TableCell>
                <TableCell align="right">Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!filteredLedgerRows.length ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No ledger rows match the current filters.
                  </TableCell>
                </TableRow>
              ) : filteredLedgerRows.map((row) => {
                const meta = importStatusMeta(row.status || (row.has_error ? 'failed' : 'succeeded'));
                const isActive = String(importOperations.tradeDetail?.trade_id || '') === String(row.trade_id || '');
                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={isActive}
                    onClick={() => handleSelectLedgerRow(row)}
                    sx={{ cursor: row.trade_id ? 'pointer' : 'default' }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatShortTimestamp(row.timestamp)}</TableCell>
                    <TableCell>{row.symbol || '—'}</TableCell>
                    <TableCell>{row.side ? <Chip size="small" variant="outlined" label={row.side} /> : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.has_error ? 'error' : meta.color}
                        icon={row.has_error ? <WarningOutlined /> : meta.icon || undefined}
                        label={row.has_error ? 'Error' : meta.label}
                        variant={meta.color === 'default' && !row.has_error ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell align="right">{formatDecimal(row.quantity, 4)}</TableCell>
                    <TableCell align="right">{formatDecimal(row.price, 4)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.commission)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color:
                          Number(row.realized_pnl) > 0
                            ? 'success.main'
                            : Number(row.realized_pnl) < 0
                            ? 'error.main'
                            : 'inherit',
                      }}
                    >
                      {formatCurrency(row.realized_pnl)}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="caption">Trade: {row.trade_id || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">Journal: {row.journal_entry_id || '—'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={row.trade_id ? 'Open trade detail' : 'No trade id returned'}>
                        <span>
                          <IconButton size="small" disabled={!row.trade_id} aria-label="Open IBKR trade detail">
                            <EyeOutlined />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderDetailBlock = (title, value) => {
    const entries = safeDetailEntries(value);
    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
        {entries.length ? (
          <Stack spacing={0.75}>
            {entries.map(([key, entry]) => (
              <Stack key={key} direction="row" spacing={1.5} justifyContent="space-between" alignItems="flex-start">
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
                  {formatStatusLabel(key)}
                </Typography>
                <Typography variant="caption" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
                  {isSensitiveDisplayKey(key) ? '••••' : formatDetailValue(entry)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">No backend fields returned.</Typography>
        )}
      </Box>
    );
  };

  const renderExecutionTable = (trade) => {
    const rows = trade.executions?.length ? trade.executions : trade.ledger_rows || [];
    return (
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Execution data</Typography>
        {!rows.length ? (
          <Typography variant="body2" color="text.secondary">No execution rows returned by the backend.</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 260 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Commission</TableCell>
                  <TableCell>Order / execution</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={detailValue(row, ['id', 'execution_id', 'exec_id'], index)}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatShortTimestamp(detailValue(row, ['ts_utc', 'timestamp', 'trade_datetime', 'trade_date', 'executed_at', 'execution_time'], ''))}
                    </TableCell>
                    <TableCell>{detailValue(row, ['side', 'action', 'buy_sell'], '—')}</TableCell>
                    <TableCell align="right">{formatDecimal(detailValue(row, ['quantity', 'qty', 'qty_signed', 'shares'], null), 4)}</TableCell>
                    <TableCell align="right">{formatDecimal(detailValue(row, ['price', 'trade_price', 'execution_price', 'avg_price'], null), 4)}</TableCell>
                    <TableCell align="right">{formatCurrency(detailValue(row, ['commission', 'commissions', 'fees', 'fee'], null))}</TableCell>
                    <TableCell>
                      {detailValue(row, ['order_id', 'orderId', 'broker_order_id'], '—')} /{' '}
                      {detailValue(row, ['execution_id', 'exec_id', 'ib_exec_id'], '—')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  const renderTradeDetailPanel = () => {
    const trade = importOperations.tradeDetail;
    if (importOperations.tradeDetailLoading) {
      return (
        <Stack spacing={1}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">Loading trade detail…</Typography>
        </Stack>
      );
    }
    if (importOperations.errors.tradeDetail) {
      return (
        <Alert severity="error">
          {extractIbkrOperationsErrorMessage(importOperations.errors.tradeDetail, 'Could not load trade detail.')}
        </Alert>
      );
    }
    if (!trade) {
      return (
        <Alert severity="info" icon={<FileSearchOutlined />}>
          Select a ledger row to review execution data, journal linkage, fees, PnL, provenance, and chart context.
        </Alert>
      );
    }

    const chartSymbol = trade.raw?.tradingview_symbol || trade.raw?.tv_symbol || trade.symbol;

    return (
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1">
              Trade detail{trade.symbol ? ` · ${trade.symbol}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Trade {trade.trade_id || '—'} · Import {trade.import_version || importOperations.selectedImportVersion || '—'}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlined />}
            disabled={!trade.trade_id || importOperations.pendingAction === 'delete-trade'}
            onClick={openDeleteTradeConfirm}
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
          >
            Delete trade
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {trade.status ? importStatusChip(trade.status, 'Trade') : null}
          {trade.side ? <Chip size="small" variant="outlined" label={`Side: ${trade.side}`} /> : null}
          {trade.quantity !== null ? <Chip size="small" variant="outlined" label={`Qty: ${formatDecimal(trade.quantity, 4)}`} /> : null}
          {trade.entry_price !== null ? <Chip size="small" variant="outlined" label={`Entry: ${formatDecimal(trade.entry_price, 4)}`} /> : null}
          {trade.exit_price !== null ? <Chip size="small" variant="outlined" label={`Exit: ${formatDecimal(trade.exit_price, 4)}`} /> : null}
          {trade.opened_at ? <Chip size="small" variant="outlined" label={`Opened: ${formatTimestamp(trade.opened_at)}`} /> : null}
          {trade.closed_at ? <Chip size="small" variant="outlined" label={`Closed: ${formatTimestamp(trade.closed_at)}`} /> : null}
        </Stack>

        {renderExecutionTable(trade)}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <Box sx={{ flex: 1 }}>{renderDetailBlock('Journal linkage', trade.journal)}</Box>
          <Box sx={{ flex: 1 }}>{renderDetailBlock('Fees / commission', trade.fees || { commission: trade.raw?.commission, fees: trade.raw?.fees })}</Box>
          <Box sx={{ flex: 1 }}>{renderDetailBlock('PnL', trade.pnl || { realized_pnl: trade.raw?.realized_pnl, net_pnl: trade.raw?.net_pnl })}</Box>
        </Stack>

        {renderDetailBlock('Normalization provenance', trade.provenance)}

        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5, pb: 0 }}>
            <LineChartOutlined />
            <Typography variant="subtitle2">Symbol analysis</Typography>
            {chartSymbol ? <Chip size="small" variant="outlined" label={chartSymbol} /> : null}
          </Stack>
          {chartSymbol ? (
            <Box sx={{ height: 320, mt: 1 }}>
              <TradingViewChart
                symbol={chartSymbol}
                height={320}
                entryPrice={trade.entry_price}
                exitPrice={trade.exit_price}
                entryTime={trade.opened_at}
                exitTime={trade.closed_at}
                tradeDirection={trade.side}
              />
            </Box>
          ) : (
            <Alert severity="info" sx={{ m: 1.5 }}>
              The selected trade does not include a symbol for chart lookup.
            </Alert>
          )}
        </Box>
      </Stack>
    );
  };

  const renderOperationsCockpit = () => {
    const selectedImportStatus =
      importOperations.selectedRefreshStatus.status ||
      importOperations.selectedImport?.status ||
      importOperations.allRefreshStatus.status;

    return (
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="overline" color="text.secondary">Import operations</Typography>
            <Typography variant="body2" color="text.secondary">
              Review imported IBKR rows, normalized trade detail, and backend processing state.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRefreshOperations}
            disabled={importOperations.loading}
            startIcon={importOperations.loading ? <CircularProgress size={16} /> : <ReloadOutlined />}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            Refresh imports
          </Button>
        </Stack>

        {renderImportStatusOverview()}

        {importOperations.errors.imports ? (
          <Alert severity="error">
            {extractIbkrOperationsErrorMessage(importOperations.errors.imports, 'Could not load IBKR imports.')}
          </Alert>
        ) : null}
        {importOperations.errors.ledger ? (
          <Alert severity="error">
            {extractIbkrOperationsErrorMessage(importOperations.errors.ledger, 'Could not load the selected import ledger.')}
          </Alert>
        ) : null}
        {operationNotice ? <Alert severity="success">{operationNotice}</Alert> : null}
        {operationError ? <Alert severity="error">{operationError}</Alert> : null}

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2">Ledger</Typography>
                <Typography variant="caption" color="text.secondary">
                  {filteredLedgerRows.length} of {importOperations.ledgerRows.length} rows shown
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                {selectedImportStatus ? importStatusChip(selectedImportStatus, 'Processing') : null}
                {importOperations.selectedRefreshStatus.checked_at ? (
                  <Chip size="small" variant="outlined" label={`Checked: ${formatShortTimestamp(importOperations.selectedRefreshStatus.checked_at)}`} />
                ) : null}
              </Stack>
            </Stack>
            {renderLedgerFilters()}
            {renderLedgerTable()}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
          {renderTradeDetailPanel()}
        </Paper>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
          <Button
            variant="outlined"
            color="error"
            startIcon={<UndoOutlined />}
            disabled={!importOperations.selectedImportVersion || importOperations.pendingAction === 'rollback-import'}
            onClick={openRollbackSelectedConfirm}
          >
            Rollback selected import
          </Button>
          <Button
            variant="text"
            color="error"
            startIcon={<UndoOutlined />}
            disabled={!importOperations.imports.length || importOperations.pendingAction === 'rollback-latest'}
            onClick={openRollbackLatestConfirm}
          >
            Rollback latest import
          </Button>
        </Stack>
      </Stack>
    );
  };

  const renderConfirmDialog = () => {
    const impactCounts = collectImpactCounts(confirmAction?.impact);
    return (
      <Dialog open={Boolean(confirmAction)} onClose={() => setConfirmAction(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{confirmAction?.title || 'Confirm action'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Alert severity="warning">{confirmAction?.description}</Alert>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Backend-reported impact</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {confirmAction?.importVersion ? <Chip size="small" variant="outlined" label={`Import: ${confirmAction.importVersion}`} /> : null}
                {confirmAction?.tradeId ? <Chip size="small" variant="outlined" label={`Trade: ${confirmAction.tradeId}`} /> : null}
                {impactCounts.map(([label, value]) => countChip(label, value, { key: label }))}
              </Stack>
              {!impactCounts.length ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  The backend did not return itemized counts for this object; the confirmation is scoped to the identifiers above.
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} disabled={Boolean(importOperations.pendingAction)}>Cancel</Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleConfirmDangerAction}
            disabled={Boolean(importOperations.pendingAction)}
            startIcon={importOperations.pendingAction ? <CircularProgress size={16} color="inherit" /> : <WarningOutlined />}
          >
            {confirmAction?.confirmLabel || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      data-testid="flyin-ibkr-flex-connection"
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: '90vw', md: '60vw', lg: '50vw' },
          minWidth: { md: 760 },
          maxWidth: '100vw',
          bgcolor: 'background.paper',
          color: 'text.primary',
          backgroundImage: 'none',
          outline: 'none',
          '& .MuiPaper-outlined': {
            bgcolor: 'background.default',
            borderColor: 'divider',
          },
        },
      }}
    >
      <Box ref={containerRef} tabIndex={-1} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflowY: 'auto' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">IBKR broker connection</Typography>
            <Typography variant="body2" color="text.secondary">
              Review live broker state, historical Flex sync, and CSV import fallback from one place.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close broker connection wizard"><CloseOutlined /></IconButton>
        </Stack>

        <Divider />

        {renderBrokerLiveState()}

        <Divider />

        <Stack spacing={1.5}>
          <Box>
            <Typography variant="overline" color="text.secondary">Historical Flex sync</Typography>
            <Typography variant="body2" color="text.secondary">
              Flex is for historical statements and reconciliation. It is separate from the broker-live health above.
            </Typography>
          </Box>

          <Alert severity={banner.severity} icon={banner.icon}>{banner.text}</Alert>

          {loading && !status.credentials_stored ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Checking Flex connection status…</Typography>
            </Stack>
          ) : null}

          {error ? (
            <Alert severity="error">{extractFlexErrorMessage(error, 'Could not load the Flex connection status.')}</Alert>
          ) : null}

          {status.credentials_stored && !showForm ? renderMaskedCredentials() : null}

          {showForm ? renderCredentialForm() : (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                onClick={handleTest}
                disabled={!status.credentials_stored || pendingAction === 'test' || syncing}
                startIcon={pendingAction === 'test' ? <CircularProgress size={16} /> : <CheckCircleOutlined />}
              >
                Test Connection
              </Button>
              <Button
                variant="outlined"
                onClick={handleSync}
                disabled={!status.connected || syncing}
                startIcon={syncing ? <CircularProgress size={16} /> : <SyncOutlined />}
              >
                Sync Flex Now
              </Button>
              <Button variant="text" onClick={() => setShowForm(true)} disabled={pendingAction === 'connect'}>
                {status.credentials_stored ? 'Update credentials' : 'Set up Flex sync'}
              </Button>
              {status.credentials_stored ? (
                <Button
                  variant="text"
                  color="error"
                  onClick={handleDisconnect}
                  disabled={pendingAction === 'delete'}
                  startIcon={pendingAction === 'delete' ? <CircularProgress size={16} color="inherit" /> : <DisconnectOutlined />}
                >
                  Disconnect
                </Button>
              ) : null}
            </Stack>
          )}

          {actionNotice ? <Alert severity="success">{actionNotice}</Alert> : null}
          {actionError ? <Alert severity="error">{actionError}</Alert> : null}

          {renderLastSync()}
        </Stack>

        <Divider />

        {renderOperationsCockpit()}

        <Divider />

        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Prefer to upload a CSV export instead? You can use that flow any time — it doesn&apos;t require Flex
            credentials.
          </Typography>
          <Button
            variant="text"
            startIcon={<UploadOutlined />}
            onClick={handleOpenCsvFallback}
            sx={{ alignSelf: 'flex-start' }}
          >
            Import CSV instead
          </Button>
        </Stack>
      </Box>

      {hasExternalCsvHandler ? null : (
        <ImportBrokerCSVFlyin open={csvOpen} onClose={() => setCsvOpen(false)} />
      )}
      {renderConfirmDialog()}
    </Drawer>
  );
}

IbkrFlexConnectionFlyin.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConnected: PropTypes.func,
  onOpenCsvImport: PropTypes.func,
};

IbkrFlexConnectionFlyin.defaultProps = {
  open: false,
  onClose: () => {},
  onConnected: () => {},
  onOpenCsvImport: undefined,
};
